import { useState } from 'react'
import { Box, Button, HStack, Input, NativeSelect, Stack, Text, VStack, Badge } from '@chakra-ui/react'
import { FiPlus, FiTrash2, FiCheck } from 'react-icons/fi'
import { toaster } from '@/components/ui/toaster'
import { extractApiMessage } from '../../lib/apiError'
import { useFollowUpKinds, useSaveFollowUpKind, useDeleteFollowUpKind } from '../../hooks/useFollowUp'
import type { FollowUpKindDto, FollowUpMode } from '../../types/followUp'

const MODE_LABELS: Record<FollowUpMode, string> = { Route: 'Route (door visit)', Call: 'Call' }

function KindRow({ kind }: { kind: FollowUpKindDto }) {
  const [name, setName] = useState(kind.name)
  const [mode, setMode] = useState<FollowUpMode>(kind.mode)
  const saveKind = useSaveFollowUpKind()
  const deleteKind = useDeleteFollowUpKind()
  const dirty = name.trim() !== kind.name || mode !== kind.mode

  const save = () =>
    saveKind.mutate(
      { id: kind.id, name: name.trim(), mode },
      {
        onError: (err) =>
          toaster.create({ title: 'Could not save kind', description: extractApiMessage(err, 'Failed to save the follow-up kind.'), type: 'error' }),
      },
    )

  const remove = () =>
    deleteKind.mutate(kind.id, {
      onError: (err) =>
        toaster.create({ title: 'Could not delete kind', description: extractApiMessage(err, 'Failed to delete the follow-up kind.'), type: 'error' }),
    })

  return (
    <Stack direction={{ base: 'column', md: 'row' }} gap={2} align={{ md: 'center' }}>
      <Input size="sm" aria-label="Kind name" value={name} onChange={(e) => setName(e.target.value)} flex={2} />
      {/* Not inside a Field.Root: it would silently disable the select. */}
      <NativeSelect.Root size="sm" flex={1}>
        <NativeSelect.Field aria-label="Kind mode" value={mode} onChange={(e) => setMode(e.target.value as FollowUpMode)}>
          <option value="Route">{MODE_LABELS.Route}</option>
          <option value="Call">{MODE_LABELS.Call}</option>
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
      <HStack gap={2}>
        {!kind.isActive && <Badge>Inactive</Badge>}
        <Button size="sm" minH="44px" variant="outline" onClick={save} disabled={!dirty || !name.trim()} loading={saveKind.isPending}>
          <FiCheck />Save
        </Button>
        <Button size="sm" minH="44px" variant="ghost" colorPalette="red" aria-label={`Delete ${kind.name}`} onClick={remove} loading={deleteKind.isPending}>
          <FiTrash2 />
        </Button>
      </HStack>
    </Stack>
  )
}

export function FollowUpKindsManager() {
  const { data: kinds, isLoading } = useFollowUpKinds()
  const saveKind = useSaveFollowUpKind()
  const [newName, setNewName] = useState('')
  const [newMode, setNewMode] = useState<FollowUpMode>('Route')

  const add = () =>
    saveKind.mutate(
      { name: newName.trim(), mode: newMode },
      {
        onSuccess: () => setNewName(''),
        onError: (err) =>
          toaster.create({ title: 'Could not add kind', description: extractApiMessage(err, 'Failed to add the follow-up kind.'), type: 'error' }),
      },
    )

  return (
    <VStack align="stretch" gap={3}>
      <Text fontSize="sm" color="ink.2">
        Custom follow-up kinds appear as "Custom: name" options on survey answers.
      </Text>
      {isLoading && <Text fontSize="sm" color="ink.2">Loading kinds...</Text>}
      {kinds?.map((k) => <KindRow key={`${k.id}:${k.name}:${k.mode}`} kind={k} />)}
      {kinds && kinds.length === 0 && <Text fontSize="sm" color="ink.2">No custom kinds yet.</Text>}
      <Box>
        <Stack direction={{ base: 'column', md: 'row' }} gap={2}>
          <Input size="sm" placeholder="New kind name" aria-label="New kind name" value={newName} onChange={(e) => setNewName(e.target.value)} flex={2} />
          <NativeSelect.Root size="sm" flex={1}>
            <NativeSelect.Field aria-label="New kind mode" value={newMode} onChange={(e) => setNewMode(e.target.value as FollowUpMode)}>
              <option value="Route">{MODE_LABELS.Route}</option>
              <option value="Call">{MODE_LABELS.Call}</option>
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          <Button size="sm" minH="44px" colorPalette="brand" onClick={add} disabled={!newName.trim()} loading={saveKind.isPending}>
            <FiPlus />Add kind
          </Button>
        </Stack>
      </Box>
    </VStack>
  )
}
